import axios from "axios";
import apiClient from "./apiClient";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const NON_IDEMPOTENT_METHODS = ["post", "patch", "put", "delete"];

/**
 * Universal API request handler.
 *
 * @param {"get"|"post"|"patch"|"put"|"delete"} method
 * @param {string} url
 * @param {object|FormData|null} data - body for post/patch/put, or query params for get/delete
 * @param {object} config - extra axios config (headers, timeout, signal, etc.)
 * @param {number} retries - retry attempts for network/5xx errors. Defaults to 0 for
 *                            non-idempotent methods (post/patch/put/delete) to avoid
 *                            duplicate side effects, 1 for get.
 *
 * @returns {Promise<{success: boolean, data?: any, message?: string, type?: string, status?: number, cancelled?: boolean}>}
 */
export const apiRequest = async (
  method,
  url,
  data = null,
  config = {},
  retries = NON_IDEMPOTENT_METHODS.includes(method) ? 0 : 1
) => {
  // Fail fast if browser knows it's offline — no point waiting for a timeout
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return { success: false, message: "You appear to be offline. Please check your connection.", type: "offline" };
  }

  try {
    const resp =
      method === "get" || method === "delete"
        ? await apiClient[method](url, { ...config, params: data })
        : await apiClient[method](url, data, config);

    return { success: true, data: resp.data, status: resp.status };
  } catch (error) {
    if (axios.isCancel(error)) {
      return { success: false, cancelled: true };
    }

    const isTimeout = error.code === "ECONNABORTED";
    const isNetworkError = !error.response;
    const isServerError = error?.response?.status >= 500;

    // Retry once on network/timeout/5xx errors, if allowed
    if ((isTimeout || isNetworkError || isServerError) && retries > 0) {
      await sleep(1000);
      return apiRequest(method, url, data, config, retries - 1);
    }

    if (isTimeout) {
      return { success: false, message: "Request timed out. Please try again.", type: "timeout" };
    }

    if (isNetworkError) {
      return { success: false, message: "Network error. Please check your internet connection.", type: "network" };
    }

    const message =
      error?.response?.data?.detail ||
      error?.response?.data?.msg ||
      error?.response?.data?.message ||
      "Something went wrong";

    return { success: false, message, type: "server", status: error.response.status };
  }
};